import { z } from "zod";

/**
 * AUDIT KAYITLARI EKRANI (CLOS-193 `[171]`).
 *
 * ### ⚠ Bu ekran hiçbir şey DEĞİŞTİRMİYOR
 *
 * Kartın kriteri: *"Düzenleme, silme, gizleme, geri yükleme veya audit olayından domain
 * işlemini geri alma aksiyonu bulunmaz."* Bu yüzden şemada hiçbir yazma girdisi yok —
 * yalnız filtre ve görünüm. Ayrım kod düzeyinde yapısal: yazma şeması olmayan bir
 * sözleşmeden yazma ucu doğmaz.
 */

/**
 * ⚠ "SONUÇ" FİLTRESİ BİLEREK YOK.
 *
 * Kart bir sonuç filtresi istiyor ama `audit_logs` böyle bir kolon taşımıyor ve `action`
 * sözlüğünde sonuç konvansiyonu da yok.
 *
 * ÖLÇÜM YÖNTEMİ (tekrar edilebilir olsun diye yazılıyor — yöntemsiz bir rakam kayıtta
 * çürük tuğladır): `apps/api/src` altında `action: "…"` yazımları toplanır, son nokta
 * parçasına indirgenir ve sayılır. 1 Eylül 2026 ölçümü: `create` 37, `created` 7,
 * `update` 9, `updated` 3 — yani aynı olay iki ayrı sözcükle yazılıyor; kimi anahtar
 * domain önekli (`contract.created`), kimi çıplak (`create`). `denied` sonekini taşıyan
 * anahtar iki tane.
 *
 * Rakamlar kod büyüdükçe değişir; DEĞİŞMEYEN yön şu: küme heterojen. Bu kümeden bir
 * ek/sonek kuralıyla "sonuç" türetmek, var olmayan bir taksonomiyi arayüzde varmış gibi
 * göstermek olurdu.
 *
 * ACIK-KARAR (CLOS-193): outcome kolonu mu, `action` konvansiyonu mu, filtre iptali mi —
 * değişmez hukuki-kanıt tablosuna kolon eklemek ve geriye doldurmak ürün sahibinin kararı.
 */
export const auditQuerySchema = z.object({
  /** Olay kategorisi = audit satırının `entity` alanı. */
  category: z.string().min(1).max(64).optional(),
  actorId: z.uuid("Geçersiz aktör kimliği").optional(),
  targetId: z.uuid("Geçersiz hedef kimliği").optional(),
  correlationId: z.string().min(1).max(128).optional(),
  from: z.iso.datetime({ message: "Geçersiz başlangıç tarihi" }).optional(),
  to: z.iso.datetime({ message: "Geçersiz bitiş tarihi" }).optional(),
  /**
   * Sayfalama imleci — bu noktadan ESKİ kayıtlar.
   *
   * ### ⚠ OPAK BİLEŞİK İMLEÇ (`<zaman>|<kimlik>`), düz tarih DEĞİL
   *
   * Önceden bu alan bir ISO tarihiydi ve **satır düşürüyordu.** Ölçüldü: aynı milisaniye
   * içinde farklı mikrosaniyeler taşıyan 6 kayıtta sayfalama yalnız 2'sini gösterdi.
   *
   * Sebep: Postgres `timestamptz` MİKROsaniye tutuyor, `Date.toISOString()` MİLİsaniyeye
   * kırpıyor. Azalan sırada kırpılmış imleç gerçek değerden KÜÇÜK kalıyor ve aradaki
   * satırlar `<` koşuluna hiç girmiyor — bir kanıt defterinde sessizce kaybolan satır.
   *
   * İmleç artık ham zaman metnini (kırpılmamış) ve satır kimliğini birlikte taşıyor.
   * Kimlik ikinci bir şeyi daha kapatıyor: aynı `created_at`'i paylaşan satırlar.
   * İstemci imleci ÇÖZMEZ, olduğu gibi geri gönderir.
   */
  before: z
    .string()
    .regex(/^[^|]{1,64}\|[0-9a-f-]{36}$/, "Geçersiz imleç")
    .optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

/**
 * Listede gösterilen audit satırı.
 *
 * ### ⚠ `before`/`after` LİSTEDE YOK
 *
 * Fark verisi yalnız DETAYDA ve orada da maskeli. Listeye koysaydık, tek bir sayfa
 * isteği yüzlerce kaydın bütün değişiklik gövdesini tarayıcıya taşırdı — hem gereksiz
 * hem de maskeleme hatasının etki alanını yüz katına çıkaran bir tasarım.
 */
export const auditEntrySchema = z.object({
  id: z.uuid(),
  action: z.string(),
  /** Olay kategorisi (varlık türü). */
  entity: z.string(),
  entityId: z.uuid(),
  /**
   * Aktörün TARİHSEL kimliği.
   *
   * ⚠ Kartın kriteri: *"Hesabı/üyeliği sona ermiş aktör tarihsel güvenli kimlikle
   * korunur; güncel rol geçmişe uygulanmaz."* Bu yüzden ad ve rol audit satırının kendi
   * alanlarından geliyor — bugünkü `users`/`memberships` tablosundan JOIN ile DEĞİL.
   * JOIN etseydik, rolü değişen birinin geçmişteki eylemleri bugünkü rolüyle görünürdü
   * ve kayıt yalan söylerdi.
   */
  actorId: z.uuid().nullable(),
  actorType: z.string(),
  actorRole: z.string().nullable(),
  /**
   * Aktörün GÖRÜNEN ADI.
   *
   * ⚠ Bu alan canlı turda eksik bulundu: satır yalnız ROLÜ gösteriyordu ve iki yöneticili
   * bir organizasyonda defter *"kim yaptı"* sorusuna cevap vermiyordu — bir kanıt defteri
   * için işlevsiz bir hâl.
   *
   * ⚠ AD bugünkü `users` kaydından, ROL satırın kendisinden geliyor ve bu ayrım
   * bilinçli. Kartın kriteri *"güncel rol geçmişe uygulanmaz"* — ROL için; ad ise kişinin
   * kimliği ve kimlik zamanla değişmiyor (rol değişiyor). Adı da satıra dondursaydık, adını
   * düzelten bir kullanıcı geçmişte eski yazımıyla kalırdı.
   *
   * ⚠ Bulunamazsa `null` — UYDURULMUYOR. Hesabı tamamen silinmiş bir aktör için istemci
   * `actorId`'ye düşüyor; "Bilinmeyen kullanıcı" yazmak, kaydın kimliği kaybettiğini
   * gizlemek olurdu.
   */
  actorName: z.string().nullable(),
  source: z.string(),
  correlationId: z.string().nullable(),
  createdAt: z.string(),
  /**
   * Hedef kayda gidilebilir mi.
   *
   * ⚠ Kartın kriteri: *"Audit görüntüleme permission'ı hedef kaydın içeriğini görme hakkı
   * değildir; kayıt erişimi yoksa hedef bağlantısı kapanır."* Karar SUNUCUDA veriliyor;
   * istemciye "bağlantı çizilebilir mi" bilgisi geliyor, kaydın kendisi değil.
   */
  targetAccessible: z.boolean(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditListSchema = z.object({
  items: z.array(auditEntrySchema),
  /** Sonraki sayfa imleci; son sayfada `null`. */
  nextCursor: z.string().nullable(),
});
export type AuditList = z.infer<typeof auditListSchema>;

/**
 * Detay görünümü.
 *
 * ### ⚠ FARKLAR MASKELİ GELİR
 *
 * Kartın kriteri: *"Parola, token, secret, tam belge metni ve maskelenmesi gereken
 * finansal/kişisel alanlar hiçbir görünümde açılmaz."* Maskeleme SUNUCUDA yapılıyor —
 * istemciye ham veri gönderip orada gizlemek, gizlenen şeyi yine de göndermek olurdu.
 */
export const auditDetailSchema = auditEntrySchema.extend({
  /** Değişmez şema sürümü — hangi kayıt biçimiyle yazıldığı. */
  chainSeq: z.number().int().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  causationId: z.string().nullable(),
  /**
   * Farklarda maskelenmiş alan bulundu mu.
   *
   * ⚠ Maskeleme SESSİZ OLMAMALI: kullanıcı boş bir alan görüp "veri yok" sanmamalı,
   * "burada bir şey var ama size gösterilmiyor" bilgisini almalı. Sessiz maskeleme,
   * eksikliği veri yokluğuyla karıştırır.
   */
  masked: z.boolean(),
});
export type AuditDetail = z.infer<typeof auditDetailSchema>;

/**
 * DIŞA AKTARMA (CLOS-194 `[172]`).
 *
 * ⚠ Talep gövdesi CLOS-193'ün filtre şemasıyla AYNI alanları taşıyor ve bu bilinçli:
 * kartın kriteri *"Export kapsamı ... CLOS-193 maskeleme kararından daha geniş olamaz."*
 * Ayrı bir filtre sözleşmesi yazsaydık, iki şema bir gün ayrışır ve export ekranda
 * görünmeyen satırları dosyaya koyabilirdi.
 */
export const auditExportRequestSchema = auditQuerySchema.omit({ before: true, limit: true });
export type AuditExportRequest = z.infer<typeof auditExportRequestSchema>;

export const AUDIT_EXPORT_STATUSES = [
  "pending",
  "processing",
  "ready",
  "failed",
  "expired",
  "cancelled",
] as const;
export type AuditExportStatus = (typeof AUDIT_EXPORT_STATUSES)[number];

export const AUDIT_EXPORT_STATUS_NAMES: Record<AuditExportStatus, string> = {
  pending: "Sırada",
  processing: "Hazırlanıyor",
  ready: "Hazır",
  failed: "Hazırlanamadı",
  expired: "Süresi doldu",
  cancelled: "İptal edildi",
};

export const auditExportSchema = z.object({
  id: z.uuid(),
  status: z.enum(AUDIT_EXPORT_STATUSES),
  filters: z.unknown(),
  snapshotAt: z.string(),
  /** Hazır olduğunda satır sayısı ve bütünlük özeti; öncesinde `null`. */
  rowCount: z.number().int().nullable(),
  checksum: z.string().nullable(),
  /**
   * ⚠ Başarısızlık SEBEBİ kullanıcıya söyleniyor.
   *
   * Kartın kriteri: *"Hazırlama/teslim hatası sahte başarı ... üretmez."* Sebebi
   * gizleseydik kullanıcı "hazırlanıyor"da kalmış sanır ve süresiz beklerdi.
   */
  failureReason: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
});
export type AuditExport = z.infer<typeof auditExportSchema>;

/**
 * İndirme bileti.
 *
 * ⚠ Ham depolama adresi YOK, kısa ömürlü imza VAR (kartın kriteri). Süre de dönüyor ki
 * arayüz "bu bağlantı n dakika geçerli" diyebilsin — sessizce ölen bir bağlantı,
 * kullanıcıya arıza gibi görünür.
 */
export const auditExportTicketSchema = z.object({
  downloadUrl: z.string(),
  expiresInSeconds: z.number().int().min(1),
  checksum: z.string(),
});
export type AuditExportTicket = z.infer<typeof auditExportTicketSchema>;
